'use client';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  SectionHeading,
  Spinner,
  StatCard,
} from '@lectura/ui';
import { Activity, AlertTriangle, BookOpen, Clock, Users } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { QueueSnapshotList } from '@/components/dashboard/QueueSnapshotList';
import { RecentPublicationsList } from '@/components/dashboard/RecentPublicationsList';
import { TrafficChart } from '@/components/dashboard/TrafficChart';
import { isIntegrationOnly, useAuth } from '@/lib/auth';
import { useDashboardSnapshot } from '@/lib/queries/dashboard';

export default function DashboardPage() {
  const router = useRouter();
  const user = useAuth((s) => s.user);
  // Integration-only usernek a dashboard-végpont 403-at ad — az ő kezdőlapja
  // az Integrációk oldal. A query-t sem indítjuk el, ne legyen felesleges 403.
  const integrationOnly = isIntegrationOnly(user);
  const { data, isLoading, isError } = useDashboardSnapshot(30, { enabled: !integrationOnly });

  useEffect(() => {
    if (integrationOnly) router.replace('/settings/integrations');
  }, [integrationOnly, router]);

  if (integrationOnly) {
    return (
      <AdminShell>
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <SectionHeading
        eyebrow="Áttekintés"
        title="Üdv újra a vezérlőpulton!"
        description={
          data
            ? `A platform legfontosabb mutatói az utóbbi ${data.windowDays} napban.`
            : 'A platform legfontosabb mutatói.'
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : isError || !data ? (
        <EmptyState
          icon={<AlertTriangle />}
          title="Nem érhető el az áttekintés"
          description="A dashboard aggregátum végpont most nem válaszol. Próbáld újra később."
        />
      ) : (
        <>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Publikált kiadványok"
              value={formatErtek(data.kpis.publishedPublications.value, 'egesz')}
              delta={data.kpis.publishedPublications.delta ?? undefined}
              icon={<BookOpen />}
            />
            <StatCard
              label="Aktív olvasók"
              value={formatErtek(data.kpis.activeReaders.value, 'egesz')}
              delta={data.kpis.activeReaders.delta ?? undefined}
              icon={<Users />}
            />
            <StatCard
              label="Olvasási percek"
              value={formatErtek(data.kpis.readingMinutes.value, 'percek')}
              delta={data.kpis.readingMinutes.delta ?? undefined}
              icon={<Clock />}
            />
            <StatCard
              label="Konverziós sikerráta"
              value={formatErtek(data.kpis.conversionSuccessRate.value, 'tizedes')}
              unit={data.kpis.conversionSuccessRate.value === null ? undefined : (data.kpis.conversionSuccessRate.unit ?? '%')}
              delta={data.kpis.conversionSuccessRate.delta ?? undefined}
              icon={<Activity />}
            />
          </div>

          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Forgalom — utolsó 14 nap</CardTitle>
              </CardHeader>
              <CardContent>
                {data.traffic.length === 0 ? (
                  <EmptyTraffic />
                ) : (
                  <TrafficChart data={data.traffic} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Konverziós sor</CardTitle>
              </CardHeader>
              <CardContent>
                <QueueSnapshotList items={data.queue} />
              </CardContent>
            </Card>
          </div>

          <div className="mt-5">
            <Card>
              <CardHeader>
                <CardTitle>Legutóbb módosított publikációk</CardTitle>
              </CardHeader>
              <CardContent>
                <RecentPublicationsList items={data.recentPublications} />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </AdminShell>
  );
}

/**
 * KPI értékének megjelenítése nullable számból.
 * `null` esetén "—" — ezzel jelezzük, hogy a metrika még nem áll rendelkezésre.
 */
function formatErtek(v: number | null, mod: 'egesz' | 'percek' | 'tizedes'): string {
  if (v === null) return '—';
  if (mod === 'egesz') return v.toLocaleString('hu-HU');
  if (mod === 'tizedes') return v.toFixed(1);
  // percek: 1284 → "1 284", 12 845 → "13k", 1 200 000 → "1,2M"
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (v >= 1_000) return `${Math.round(v / 1000)}k`;
  return v.toLocaleString('hu-HU');
}

function EmptyTraffic() {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-brand-50 to-accent-50 px-6 text-center text-sm text-[var(--color-foreground-muted)]">
      <span className="text-base font-semibold">Még nincs forgalmi adat</span>
      <span>
        Olvasási esemény nem érkezett a vizsgált időszakban. Amint a reader-svc
        eseményeket kezd publikálni, a diagram automatikusan feltöltődik.
      </span>
    </div>
  );
}
